/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {FormHelperText, FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Split} from '@dropbox/dig-foundations';
import {hasReadableText, isHexColor} from 'views/home/bannerColors';

import styles from './HomeBanners.module.css';

/**
 * A banner color, as a picker and a hex field. Every edit reaches the form, so
 * a half-typed or invalid value blocks saving instead of silently keeping the
 * previous color.
 */
export const BannerColorInput = ({
  id,
  label,
  value,
  textMode,
  helperText,
  onChange,
  onClear,
}: {
  id: string;
  label: string;
  /** What the field shows; may be invalid while the admin types. Empty when unset. */
  value: string;
  /** The text color that sits on this background, for the contrast check. */
  textMode: 'bright' | 'dark';
  helperText?: string;
  /** Called with every edit: a valid color in uppercase, or the text as typed. */
  onChange: (value: string) => void;
  /** Offer to clear an optional color. */
  onClear?: () => void;
}) => {
  const isSet = value !== '';
  const isValid = isHexColor(value);
  const isUnreadable = isValid && !hasReadableText(value, textMode);

  return (
    <FormRow>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <Split gap="8" alignY="center">
        <Split.Item>
          <input
            type="color"
            aria-label={`${label} picker`}
            className={styles.colorPicker}
            value={isValid ? value.toLowerCase() : '#000000'}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
          />
        </Split.Item>
        <Split.Item>
          <TextInput
            id={id}
            size="small"
            className={styles.hexInput}
            placeholder={onClear ? 'None' : undefined}
            value={value}
            isInvalid={isSet ? !isValid : !onClear}
            aria-describedby={`${id}-help`}
            onChange={(e) => {
              const next = e.currentTarget.value.trim();
              if (next === '' && onClear) {
                onClear();
              } else {
                onChange(isHexColor(next) ? next.toUpperCase() : next);
              }
            }}
          />
        </Split.Item>
        {onClear && isSet && (
          <Split.Item>
            <Button variant="transparent" size="small" onClick={onClear}>
              Clear
            </Button>
          </Split.Item>
        )}
      </Split>
      <FormHelperText id={`${id}-help`}>
        {isSet && !isValid
          ? 'Use six hex digits, like #1A2B3C.'
          : isUnreadable
            ? `Text is hard to read on this color. Try a ${textMode === 'bright' ? 'lighter' : 'darker'} one.`
            : helperText}
      </FormHelperText>
    </FormRow>
  );
};
